import React, { useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Button,
  Card,
  CardBody,
  CardTitle,
  ClipboardCopyButton,
  CodeBlock,
  CodeBlockAction,
  CodeBlockCode,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Grid,
  GridItem,
  Spinner,
  Tooltip,
} from '@patternfly/react-core';
import { DownloadIcon, SyncAltIcon } from '@patternfly/react-icons';
import { useCertificateChain } from '../../../hooks/useCertificateChain';
import { useCertificatePem } from '../../../hooks/useCertificatePem';
import { downloadTextFile, slugifyForFilename } from '../../../utils/fileDownload';

interface CertificatePemTabProps {
  certificateId: number;
}

interface PemCodeBlockProps {
  // Raw PEM value (trailing newline included) — copy and download use it
  // verbatim; only the on-screen rendering is trimmed.
  pem: string;
  idPrefix: string;
  copyAriaLabel: string;
  downloadFilename: string;
  downloadAriaLabel: string;
  refreshAriaLabel: string;
  refreshing: boolean;
  onRefresh: () => void;
}

function PemCodeBlock({
  pem,
  idPrefix,
  copyAriaLabel,
  downloadFilename,
  downloadAriaLabel,
  refreshAriaLabel,
  refreshing,
  onRefresh,
}: PemCodeBlockProps) {
  const [copied, setCopied] = useState<boolean>(false);

  const actions = (
    <>
      <CodeBlockAction>
        <ClipboardCopyButton
          id={`${idPrefix}-copy`}
          aria-label={copyAriaLabel}
          variant="plain"
          onClick={() => {
            void navigator.clipboard.writeText(pem);
            setCopied(true);
          }}
          exitDelay={copied ? 1500 : 600}
          onTooltipHidden={() => setCopied(false)}
        >
          {copied ? 'Copied' : 'Copy'}
        </ClipboardCopyButton>
      </CodeBlockAction>
      <CodeBlockAction>
        <Tooltip content="Download">
          <Button
            variant="plain"
            aria-label={downloadAriaLabel}
            icon={<DownloadIcon />}
            onClick={() => downloadTextFile(downloadFilename, pem)}
          />
        </Tooltip>
      </CodeBlockAction>
      <CodeBlockAction>
        <Tooltip content="Refresh">
          <Button
            variant="plain"
            aria-label={refreshAriaLabel}
            icon={refreshing ? <Spinner size="sm" aria-label={refreshAriaLabel} /> : <SyncAltIcon />}
            // aria-disabled keeps the control hoverable/focusable so the
            // tooltip works during the refetch (PF prevents the click).
            isAriaDisabled={refreshing}
            onClick={onRefresh}
          />
        </Tooltip>
      </CodeBlockAction>
    </>
  );

  return (
    <CodeBlock actions={actions}>
      <CodeBlockCode>{pem.trimEnd()}</CodeBlockCode>
    </CodeBlock>
  );
}

interface FullChainPaneBodyProps {
  certificateId: number;
}

/**
 * Body of the "Full chain" pane. Fetches the per-certificate chain endpoint —
 * a live walk from the leaf's ACTUAL issuer on the platform — so it works on
 * every platform type (on ACME platforms the issuing intermediate varies per
 * certificate; the platform-level CA bundle could never answer this). The
 * response is already in fullchain order: leaf first, then issuers towards
 * the root.
 */
function FullChainPaneBody({ certificateId }: FullChainPaneBodyProps) {
  const { chain, loading, error, errorStatus, refetch } = useCertificateChain(certificateId);

  if (loading && chain === null) {
    return (
      <EmptyState
        titleText="Retrieving certificate chain…"
        icon={() => <Spinner size="lg" aria-label="Retrieving certificate chain" />}
        variant="sm"
        headingLevel="h3"
      >
        <EmptyStateBody>Reading the trust chain live from the platform. This can take a few seconds.</EmptyStateBody>
      </EmptyState>
    );
  }

  // 409 (not issued yet) and 404 (row pruned by the sync since the page
  // loaded) are expected lifecycle states, not faults — mirror the
  // certificate pane's neutral handling with the backend's message as-is.
  if (error !== null && chain === null && (errorStatus === 404 || errorStatus === 409)) {
    return (
      <EmptyState
        titleText={errorStatus === 409 ? 'Certificate not issued yet' : 'Certificate chain unavailable'}
        status="info"
        variant="sm"
        headingLevel="h3"
      >
        <EmptyStateBody>{error}</EmptyStateBody>
      </EmptyState>
    );
  }

  const errorAlert = error !== null && (
    <Alert
      variant="danger"
      isInline
      title={
        errorStatus === 502
          ? 'The certificate platform could not provide the chain'
          : 'Could not load the certificate chain'
      }
      actionLinks={<AlertActionLink onClick={() => void refetch()}>Retry</AlertActionLink>}
      style={{ marginBottom: '1rem' }}
    >
      {error}
    </Alert>
  );

  if (chain === null) {
    return <>{errorAlert}</>;
  }

  return (
    <>
      {errorAlert}
      {!chain.complete && (
        <Alert variant="warning" isInline title="Certificate chain is incomplete" style={{ marginBottom: '1rem' }}>
          The platform does not hold the parent CA of the last chain entry; the chain below is partial. On ACME
          platforms (Let&apos;s Encrypt) this is the normal state — the external root CA is not imported, and TLS
          servers expect the fullchain without it.
        </Alert>
      )}
      <PemCodeBlock
        pem={chain.pem}
        idPrefix="certificate-fullchain"
        copyAriaLabel="Copy full certificate chain"
        downloadFilename={`${slugifyForFilename(chain.cn)}-fullchain.pem`}
        downloadAriaLabel="Download full certificate chain"
        refreshAriaLabel="Refresh certificate chain"
        refreshing={loading}
        onRefresh={() => void refetch()}
      />
    </>
  );
}

/**
 * "PEM" tab of the certificate detail page, split in two panes:
 *
 * - **Certificate** — the raw leaf block (`-----BEGIN CERTIFICATE-----` …),
 *   served from the local cache by the Download endpoint's default mode.
 * - **Full chain** — the live per-certificate chain from the platform, in
 *   fullchain order (leaf, issuing CA, …). Available on every platform type,
 *   so there is no capability gate here — the backend arbitrates errors.
 *
 * Both fetch their own data, so the tab must stay mounted lazily under the
 * parent's `mountOnEnter`, and each pane carries its own Refresh.
 */
function CertificatePemTab({ certificateId }: CertificatePemTabProps) {
  const { download, loading, error, errorStatus, refetch } = useCertificatePem(certificateId);

  if (loading && download === null) {
    return (
      <EmptyState
        titleText="Loading certificate PEM…"
        icon={() => <Spinner size="lg" aria-label="Loading certificate PEM" />}
        variant="lg"
        headingLevel="h2"
      >
        <EmptyStateBody>Reading the certificate from the local cache.</EmptyStateBody>
      </EmptyState>
    );
  }

  // 409 (not issued yet) and 404 (no PEM synced locally yet, or the row was
  // pruned by the sync since the page loaded) are expected lifecycle states,
  // not faults — render them as neutral empty states with the backend's
  // message as-is; the 404 title stays generic since it covers both triggers.
  if (error !== null && download === null && (errorStatus === 404 || errorStatus === 409)) {
    return (
      <EmptyState
        titleText={errorStatus === 409 ? 'Certificate not issued yet' : 'Certificate PEM unavailable'}
        status="info"
        variant="lg"
        headingLevel="h2"
      >
        <EmptyStateBody>{error}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" icon={<SyncAltIcon />} onClick={() => void refetch()}>
              Refresh
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  const errorAlert = error !== null && (
    <Alert
      variant="danger"
      isInline
      title="Failed to load the certificate PEM"
      actionLinks={<AlertActionLink onClick={() => void refetch()}>Retry</AlertActionLink>}
      style={{ marginBottom: '1rem' }}
    >
      {error}
    </Alert>
  );

  if (download === null) {
    return <div style={{ marginTop: '1rem' }}>{errorAlert}</div>;
  }

  return (
    <div style={{ marginTop: '1rem' }}>
      {errorAlert}
      <Grid hasGutter md={6}>
        <GridItem>
          <Card isFullHeight>
            <CardTitle>Certificate</CardTitle>
            <CardBody>
              <PemCodeBlock
                pem={download.pem}
                idPrefix="certificate-pem"
                copyAriaLabel="Copy certificate PEM"
                downloadFilename={`${slugifyForFilename(download.cn)}.crt`}
                downloadAriaLabel="Download certificate PEM"
                refreshAriaLabel="Refresh certificate PEM"
                refreshing={loading}
                onRefresh={() => void refetch()}
              />
            </CardBody>
          </Card>
        </GridItem>
        <GridItem>
          <Card isFullHeight>
            <CardTitle>Full chain</CardTitle>
            <CardBody>
              <FullChainPaneBody certificateId={certificateId} />
            </CardBody>
          </Card>
        </GridItem>
      </Grid>
    </div>
  );
}

export default CertificatePemTab;
